import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getPortraitViewportContainer } from "../../compoment/PortraitStage";
import QRScanner from "../../compoment/features/QRScanner";
import { CircleBack, Info } from "../../compoment/icon";
import { useRouteFrozen } from "../../routes/routeTransition";
import qrTicketIcon from "../../assets/img/qr-ticket.png";
import { useLocation, useNavigate } from "react-router-dom";
import { resolveNextCategoryInSequence, verifyTicketStatusApi } from "../../api";
import SuccessScanModal from "../../compoment/modals/SuccessScanModal";
import Loader from "../../compoment/ui/Loader";
import ExpiredModal from "../../modal/ExpiredModal";
import FailureScanModal from "../../compoment/modals/FailureScanModal";
import { TICKET_EXTENDED_EVENT } from "../../constants/events";
import { toast } from "react-toastify";

const DEFAULT_EXPIRED_TICKET_MESSAGE =
  "This ticket has expired. Please purchase a new ticket.";

const DEFAULT_INVALID_MESSAGE =
  "Invalid ticket. Check ticket date & time.";

const getTicketIdFromScan = (decodedText: string) => {
  const value = decodedText.trim();

  try {
    const scannedUrl = new URL(value);
    return scannedUrl.searchParams.get("ticketId") || value;
  } catch {
    return value;
  }
};

const getExtendTicketUrl = (ticketId: string): string => {
  const webUrl = process.env.REACT_APP_FRONTEND_BASE_URL?.trim();
  if (!webUrl) {
    console.error(
      "REACT_APP_FRONTEND_BASE_URL is not set. Cannot build extend-ticket URL."
    );
    return "";
  }

  try {
    const extendUrl = new URL("/extend-ticket", webUrl);
    extendUrl.searchParams.set("ticketId", ticketId);
    return extendUrl.toString();
  } catch (error) {
    console.error("Invalid REACT_APP_FRONTEND_BASE_URL:", webUrl, error);
    return "";
  }
};

type ScanTicketResponse = {
  message?: string;
  validate?: boolean;
  ticket?: {
    id: string;
    first_name?: string;
    last_name?: string;
    ticket_status?: string;
    [key: string]: unknown;
  };
  isExpired?: boolean;
  isExtendable?: boolean;
  canExtend?: boolean;
  ticketStatus?: string;
};

const isTicketExtendable = (data: {
  isExtendable?: boolean;
  canExtend?: boolean;
}) => data.isExtendable === true || data.canExtend === true;

const QRScanScreen = () => {
  const [hasScannedError, setHasScannedError] = useState(false);
  const [successScan, setSuccessScan] = useState(false);
  const [expiredModal, setExpiredModal] = useState(false);
  const [expiredTicketId, setExpiredTicketId] = useState("");
  const [expiredMessage, setExpiredMessage] = useState(
    DEFAULT_EXPIRED_TICKET_MESSAGE
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(true);
  const [scannerRestart, setScannerRestart] = useState(0);
  const navigate = useNavigate();
  const routeFrozen = useRouteFrozen();
  const location = useLocation() as { state?: { rescan?: number } };

  useEffect(() => {
    if (!location.state?.rescan) return;

    setExpiredModal(false);
    setHasScannedError(false);
    setScannerRestart((count) => count + 1);
    setShowScanner(false);

    const timer = setTimeout(() => {
      setShowScanner(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [location.state?.rescan]);

  useEffect(() => {
    const closeExpiredModal = () => {
      setExpiredModal(false);
      setHasScannedError(false);
    };

    window.addEventListener(TICKET_EXTENDED_EVENT, closeExpiredModal);
    return () =>
      window.removeEventListener(TICKET_EXTENDED_EVENT, closeExpiredModal);
  }, []);

  const handleSuccess = async (decodedText: string) => {
    const ticketId = getTicketIdFromScan(decodedText);
    if (!ticketId) return;

    try {
      setIsLoading(true);

      const payload = { ticketId };
      const response = await verifyTicketStatusApi(payload);
      const data = (response?.data || {}) as ScanTicketResponse;
      const ticket = data.ticket;
      console.log("API response data:", data);

      if (data.isExpired || ticket?.ticket_status?.toLowerCase() === "expired") {
        const message = data.message || DEFAULT_EXPIRED_TICKET_MESSAGE;

        if (isTicketExtendable(data)) {
          setExpiredTicketId(ticket?.id || ticketId);
          setExpiredMessage(message);
          if (ticket) {
            try {
              localStorage.setItem("ticket_detail", JSON.stringify(ticket));
            } catch {
              // ignore storage errors
            }
          }
          setExpiredModal(true);
        } else {
          setExpiredMessage(message);
          setHasScannedError(true);
        }
        return;
      }

      if (data.validate === true && ticket?.id) {
        setSuccessScan(true);
        try {
          localStorage.setItem("ticket_detail", JSON.stringify(ticket));
        } catch (e) {
          // ignore storage errors
        }

        let nextCategory = null;
        try {
          nextCategory = await resolveNextCategoryInSequence();
        } catch (error) {
          console.error("Failed to resolve next category:", error);
        }

        if (!nextCategory) {
          setSuccessScan(false);
          toast.error("Unable to assign a category. Please try again.");
          setHasScannedError(true);
          setExpiredMessage("Unable to assign a category. Please try again.");
          return;
        }

        setTimeout(() => {
          setSuccessScan(false);
          navigate("/main", {
            state: {
              category: nextCategory.name,
              categoryObject: nextCategory,
            },
          });
        }, 2000);
        return;
      }

      const message = data.message || DEFAULT_INVALID_MESSAGE;
      setExpiredMessage(message);
      setHasScannedError(true);
    } catch (err: unknown) {
      const errorData =
        (err as { response?: { data?: ScanTicketResponse } })?.response
          ?.data || {};
      const errorMessage = errorData?.message || "";
      const isExpiredTicket =
        typeof errorMessage === "string" &&
        errorMessage.toLowerCase().includes("expired");

      if (isExpiredTicket) {
        const message = errorMessage || DEFAULT_EXPIRED_TICKET_MESSAGE;

        if (isTicketExtendable(errorData)) {
          setExpiredTicketId(ticketId);
          setExpiredMessage(message);
          setExpiredModal(true);
        } else {
          setExpiredMessage(message);
          setHasScannedError(true);
        }
      } else {
        const message = errorMessage || DEFAULT_INVALID_MESSAGE;
        setExpiredMessage(message);
        setHasScannedError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFailure = (error: string) => {
    setHasScannedError(true);
  };

  const restartScanner = () => {
    setScannerRestart((count) => count + 1);
  };

  return (
    <>
      {createPortal(
        <div className="fullScreenBg" />,
        getPortraitViewportContainer() ?? document.body
      )}
      {isLoading && <Loader />}
      <section className="scanScreen">
        <button
          type="button"
          className="scanScreen__back"
          aria-label="Back"
          onClick={() => navigate("/", { replace: true })}
        >
          <CircleBack />
        </button>
        <div className="scanScreen__hint">
          <span>
            <Info />
          </span>
          Please make sure the QR Code is clearly visible to the camera
        </div>

        <div className="scanScreen__camera">
          <QRScanner
            onScanSuccess={handleSuccess}
            onScanFailure={handleFailure}
            hasScannedError={hasScannedError}
            setHasScannedError={setHasScannedError}
            showScanner={showScanner && !routeFrozen}
            restartTrigger={scannerRestart}
          />
          <div className="scanScreen__frame" aria-hidden="true">
            <span className="scanScreen__corner scanScreen__corner--tl" />
            <span className="scanScreen__corner scanScreen__corner--tr" />
            <span className="scanScreen__corner scanScreen__corner--bl" />
            <span className="scanScreen__corner scanScreen__corner--br" />
          </div>
        </div>

        <div className="scanScreen__card">
          <span className="scanScreen__cardIcon">
            <img src={qrTicketIcon} alt="" />
          </span>
          <div className="scanScreen__cardText">
            <p className="scanScreen__cardTitle">Show your QR ticket</p>
            <p className="scanScreen__cardSub">
              Hold your QR ticket steady in front of the camera.
            </p>
          </div>
        </div>
      </section>
      {successScan && (
        <SuccessScanModal
          show={successScan}
          onHide={() => setSuccessScan(false)}
        />
      )}
      {hasScannedError && (
        <FailureScanModal
          show={hasScannedError}
          message={expiredMessage}
          onHide={() => setHasScannedError(false)}
        />
      )}
      {expiredModal && <ExpiredModal
        show={expiredModal}
        message={expiredMessage}
        extendTicketUrl={getExtendTicketUrl(expiredTicketId)}
        onHide={() => {
          setExpiredModal(false);
          setHasScannedError(false);
          restartScanner();
        }}
      />}
    </>
  );
};

export default QRScanScreen;
